import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, Link, useLocation } from "react-router-dom"
import { toast } from 'react-toastify'
import { FaEdit } from "react-icons/fa"
import Server from "../../Shared/Server"
import { FaFileExport } from "react-icons/fa"
import Spinner from '../../Shared/Spinner'
import SelectField from "../../Shared/SelectField";
import {years_list, months_list} from "../../Shared/List";
import AsyncSelectField from "../../Shared/AsyncSelect"
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const MonthlyVoucherSummary = (props) => {
    const current_user = JSON.parse(localStorage.getItem('user'))
    const billing_date  = current_user?.branch?.billing_date
    const [errors, setErrors] = useState({});
    const [vouchers, setVouchers] = useState([]);
    const [collectorOptions, setCollectorOptions] = useState([]);
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([{}]);
    const [title, setTitle] = useState('');
    const [yearOptions, setYearOptions] = useState([])
    const [monthOptions, setMonthOptions] = useState([])
    const [monthName, setMonthName] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const gridRef = useRef();
    const navigate = useNavigate()
    const location = useLocation()

    const [formData, setFormData] = useState({
        year: billing_date.year,
        month: billing_date.month,
        collector_id: 0,
    })
    const {year, month, collector_id} = formData

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },

        {
            headerName: "Book",
            field: "book",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Locations",
            field: "locations",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Due",
            field: "due",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Paid",
            field: "paid",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Discount",
            field: "discount",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Balance",
            field: "balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Actions",
            pinned: 'left',
            minWidth: 80,
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return '';
                return (
                    <div className="d-flex gap-1">
                        <Link
                            className="btn btn-outline-info"
                            title="Edit voucher"
                            to="/update-book-vouchers"
                            state={{ payload: {
                                year: year,
                                month: month,
                                book: e.data.book,
                            } }}
                        >
                            <FaEdit/>
                        </Link>
                    </div>
                );
            },
        },
    ], [year, month]);

    const fetchData = async (payload) => {
        setTitle(`${months_list[formData.month - 1].name} ${year} Vouchers Report`)
        setIsLoading(true)
        try {
            const response = await Server.get(`/reports/monthly-voucher-summary/?year=${payload.year}&month=${payload.month}&collector_id=${collector_id}`)
            const years = []
            const months = []

            setMonthName(months_list[formData.month - 1].name)

            years_list.map(year => (
                years.push({
                    id: year.id,
                    key: year.key,
                    name: year.name
                })
            ))

            months_list.map(month => (
                months.push({
                    id: month.id,
                    key: month.key,
                    name: month.name
                })
            ))

            setVouchers(response?.data)
            setYearOptions(years)
            setMonthOptions(months)
            setIsLoading(false)

            const output = response?.data.reduce((data, row) => {
                data.locations += parseInt(row.locations);
                data.due += parseFloat(row.due);
                data.paid += parseFloat(row.paid);
                data.discount += parseFloat(row.discount);
                data.balance += parseFloat(row.balance);
                return data;
            }, {locations: 0, due: 0.00, paid: 0.00, discount: 0.00, balance: 0});
            setPinnedBottomRowData([
                {
                    locations: parseFloat(output.locations).toFixed(0),
                    due: parseFloat(output.due).toFixed(2),
                    paid: parseFloat(output.paid).toFixed(2),
                    discount: parseFloat(output.discount).toFixed(2),
                    balance: parseFloat(output.balance).toFixed(2)
                }
            ]);

        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 422) {
                setErrors({})
                error.response?.data?.detail.map((err) => (
                    setErrors((prevState) => ({
                        ...prevState,
                        [err.loc[1]]: err.msg
                    }))
                ))
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else {
                toast.warning(error?.response?.data?.detail || 'Failed to load voucher summary')
            }
        }
    }

    useEffect(() => {
        if (location.state?.year && location.state?.month) {
            setFormData(prev => ({
                ...prev,
                year: location.state.year,
                month: location.state.month,
            }))
            fetchData({ year: location.state.year, month: location.state.month })
        } else {
            fetchData({year: year, month: month})
        }

    }, [])

      const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))

        // set month name when ever month is changen
        if (e.target.name === 'month'){
            setMonthName(months_list[formData.month - 1].name)
        }

    }

    const onDoubleClick = (payload) => {
        navigate(`/monthly-voucher-detail`, { state: {payload: payload}})
    }

    const onSubmit = (e) => {
        e.preventDefault();

        const data = {year, month, collector_id}
        fetchData(data)

    }

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    const gridApiRef = useRef(null);

    const onGridReady = (params) => {
        gridApiRef.current = params.api;
    };

    const onExportClick = () => {
        if (gridApiRef.current) {
            gridApiRef.current.exportDataAsCsv({ fileName: `${title}.csv` });
        } else {
            console.error("Grid API is not available yet!");
        }
    };

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    // Load options dynamically from the api
    const loadOptions = async (query) => {
        try {
        const response = await Server.get('/options/collectors', {
            params: { q: query },
        });
        setCollectorOptions(response.data)
        return response.data;
        } catch (error) {
        return [];
        }
    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <SelectField
                        name="year"
                        value={parseInt(year)}
                        options={yearOptions}
                        onChange={onChange}
                        error={errors.year}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="month"
                        value={parseInt(month)}
                        options={monthOptions}
                        onChange={onChange}
                        error={errors.month}
                    />
                </div>
                <div className="col-md-3">
                    <AsyncSelectField
                        name="collector_id"
                        value={collector_id}
                        onChange={onChange}
                        loadOptions={loadOptions}
                        options={collectorOptions}
                        placeholder="Start typing to search..."
                        error={errors.collector_id}
                    />
                </div>
                <div className="col-md-4">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-1">
                    <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
                </div>
            </div>
            <br/>
        </form>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            onRowDoubleClicked={(e) => {
                                if (e.node.rowPinned) return '';
                                if (e.data.locations < 1) return '';
                                onDoubleClick({
                                    year: parseInt(year),
                                    month: parseInt(month),
                                    book: e.data.book,
                                })
                            }}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            defaultColDef={defaultColDef}
                            suppressMenuHide={true}

                            rowData={vouchers}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}


                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  )
}

export default MonthlyVoucherSummary